import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { EditCell } from "@/components/canvas/EditCell";
import { longDate, basisOf } from "@/lib/sk";
import { BasisMark } from "@/components/canvas/BasisMark";

const FACTS = [
  ["tender_deadline", "Tilbudsfrist", "date"],
  ["confirm_by", "Bekreft deltakelse", "date"],
  ["questions_by", "Spørsmålsfrist", "date"],
  ["period_from", "Levering fra", "date"],
  ["period_to", "til", "date"],
  ["commissioning_days", "Igangkjøringsdager", "text"],
  ["award_expected", "Tildeling forventet", "text"],
];
const MONTHS = ["januar", "februar", "mars", "april", "mai", "juni", "juli", "august", "september", "oktober", "november", "desember"];

/** «1.–8. juni 2026» when the stay is within one month, «28. mai – 8. juni 2026» across months. Never invents a date. */
export function periodText(from, to) {
  if (!from) return null;
  const a = new Date(from), b = to ? new Date(to) : null;
  if (Number.isNaN(a.getTime())) return String(from);
  if (!b || Number.isNaN(b.getTime())) return longDate(from);
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) return `${a.getDate()}.–${b.getDate()}. ${MONTHS[a.getMonth()]} ${a.getFullYear()}`;
  if (a.getFullYear() === b.getFullYear()) return `${a.getDate()}. ${MONTHS[a.getMonth()]} – ${b.getDate()}. ${MONTHS[b.getMonth()]} ${a.getFullYear()}`;
  return `${longDate(from)} – ${longDate(to)}`;
}

function Fact({ k, label, type, value, onSave, basis, onConfirm }) {
  const display = type === "date" && value ? longDate(value) : value;
  return (
    <div className="min-w-0" data-testid={`krav-fact-${k}`}>
      <div className="text-[12.5px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[14px] text-foreground/90">
        <EditCell value={value} display={display} onSave={(v) => onSave(k, v)} testId={`krav-fact-${k}-edit`} placeholder="ikke oppgitt" allowEmpty title={type === "date" ? "ÅÅÅÅ-MM-DD" : undefined} />
      </div>
      {value ? <BasisMark basis={basis} onConfirm={() => onConfirm(k)} testId={`krav-fact-${k}-basis`} compact hideConfirmed /> : null}
    </div>
  );
}

function Group({ title, count, children, testId, hint }) {
  if (!count) return null;
  return (
    <div className="pt-4 first:pt-0" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="text-[13.5px] font-medium text-foreground/80">{title} <span className="ml-1 font-mono text-[11.5px] font-normal text-muted-foreground">{count}</span></h4>
        {hint ? <span className="text-[12px] text-muted-foreground/80">{hint}</span> : null}
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/** Everything else from the request: all dates (editable), rules, deliverables, and what the document reading observed. */
export function CaseDetails({ project, ctx, ops }) {
  const [open, setOpen] = useState(false);
  const f = project.facts || {};
  const save = (k, v) => ops.patchCase({ facts: { [k]: v } });
  const confirm = (k) => ops.patchCase({ confirm: [k] }, "Bekreftet");
  const toggleDeliverable = (i) => ops.patchCase({ facts: { deliverables: (f.deliverables || []).map((d, j) => (j === i ? { ...d, done: !d.done } : d)) } });
  const observations = project.open_questions || [];
  return (
    <section className="mt-5 border-t border-black/[0.06]" data-testid="krav-details">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 py-3 text-left text-[14px] font-medium text-foreground/80 hover:text-foreground" data-testid="krav-details-toggle">
        <span>Saksdetaljer <span className="ml-1 text-[12.5px] font-normal text-muted-foreground">frister, spilleregler, leveransekrav{observations.length ? ", observasjoner" : ""}</span></span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div className="pb-5" data-testid="krav-details-body">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
            {FACTS.map(([k, label, type]) => <Fact key={k} k={k} label={label} type={type} value={f[k]} onSave={save} basis={basisOf(project, k)} onConfirm={confirm} />)}
          </div>
          {f.period_note ? <p className="mt-3 text-[13px] leading-[1.55] text-muted-foreground" data-testid="krav-fact-period-note">{f.period_note}</p> : null}
          <div className="mt-4 divide-y divide-black/[0.05]">
            <Group title="Spilleregler fra forespørselen" count={(f.rules || []).length} testId="krav-rules">
              <ul className="space-y-1.5">{(f.rules || []).map((r, i) => <li key={i} className="flex gap-2.5 text-[14px] leading-[1.55] text-foreground/85"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--brand))]" />{r.text || r}</li>)}</ul>
            </Group>
            <Group title="Leveransekrav til tilbudet" count={(f.deliverables || []).length} testId="krav-deliverables">
              <ul className="space-y-1">
                {(f.deliverables || []).map((d, i) => (
                  <li key={i}>
                    <label className="flex cursor-pointer items-start gap-2.5 rounded-md px-1 py-1 text-[14px] leading-[1.5] hover:bg-black/[0.03]">
                      <input type="checkbox" checked={Boolean(d.done)} onChange={() => toggleDeliverable(i)} className="mt-[4px] h-3.5 w-3.5 accent-[hsl(var(--brand))]" data-testid={`krav-deliverable-${i}`} />
                      <span className={cn(d.done && "text-muted-foreground line-through")}>{d.text || d}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </Group>
            <Group title="Observasjoner fra dokumentlesingen" count={observations.length} testId="krav-open-questions" hint="Hva lesingen ikke fant – ikke nødvendigvis en mangel i saken">
              <ul className="space-y-1.5">{observations.map((q, i) => <li key={i} className="flex gap-2.5 text-[14px] leading-[1.55] text-foreground/80"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-black/20" />{q}</li>)}</ul>
            </Group>
          </div>
        </div>
      ) : null}
    </section>
  );
}
